// ===== CONFIG =====
//
// Centralises the two pieces of runtime context every API call needs:
//   1. The API base URL — read from VITE_API_BASE_URL, never hardcoded paths.
//   2. The event slug — taken from the URL path (`/{slug}`), per the API spec.
//
// All endpoints live under `${API_BASE}/api`. A token from one event will not
// work on another, so the slug is the single thing that scopes this gallery to
// the correct couple's submissions — we never send an event_id, and the API
// resolves the event from the slug server-side.

// Production default is the admin API host. Override in dev via a `.env`:
//   VITE_API_BASE_URL=http://127.0.0.1:8000
export const API_BASE = (
    import.meta.env.VITE_API_BASE_URL || 'https://admin-vgb.raikancinta.com'
).replace(/\/+$/, ''); // strip any trailing slash so we control the joins

/**
 * Resolve the event slug for this page load.
 *
 * Priority:
 *   1. First non-empty path segment, e.g. `/sarah-ali-x7k2` -> `sarah-ali-x7k2`.
 *      (Ignores a leading sub-path the build may be served under is NOT assumed;
 *      the gallery is expected to be served at the domain root per the spec.)
 *   2. `?slug=` / `?event=` query param (handy for local dev under a sub-path).
 *
 * Returns `null` when no slug can be determined — the app then shows a friendly
 * "this link isn't valid" message rather than guessing an event.
 *
 * @returns {string|null}
 */
export function resolveSlug() {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('slug') || params.get('event');
    if (fromQuery) return fromQuery.trim();

    const segments = window.location.pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
        // Drop a trailing "index.html" if present (static hosting).
        const last = segments[segments.length - 1];
        const candidate = last.toLowerCase() === 'index.html'
            ? segments[segments.length - 2]
            : last;
        if (candidate) return decodeURIComponent(candidate).trim();
    }

    return null;
}
